import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera,
  Trash,
  CheckCircle,
  X,
  ShareNetwork,
  EnvelopeSimple,
  ChatText,
  Copy,
  Play,
  Globe,
  Star,
  NotePencil,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { PhotoEditor } from "@/components/PhotoEditor";
import { Slideshow } from "@/components/Slideshow";
import { photoSrc } from "@/components/Thumb";
import { BeforePhotoControl } from "@/components/BeforePhotoControl";
import { ProjectStoryDialog } from "@/components/ProjectStoryDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkAll } from "@/lib/shrinkImage";
import { HD_STATUS_LABEL, hasPendingHd, usePendingHdRefresh } from "@/lib/photoHd";

const CATEGORIES = [
  "Kitchen",
  "Island",
  "Bath",
  "Fireplace",
  "Outdoor",
  "Commercial",
  "Other",
];

export default function CompletionPhotos() {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [slabs, setSlabs] = useState([]);
  const [q, setQ] = useState("");
  const [jobFilter, setJobFilter] = useState([]);
  const [form, setForm] = useState({
    job_id: "none",
    slab_color: "",
    slab_id: "none",
    description: "",
    name: "",
    category: "none",
  });
  const [catFilter, setCatFilter] = useState([]);
  const [webFilter, setWebFilter] = useState("all");
  const [featFilter, setFeatFilter] = useState("all");
  const [slide, setSlide] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [files, setFiles] = useState([]);
  const [editing, setEditing] = useState(false);
  const [story, setStory] = useState(null);
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [picked, setPicked] = useState([]);
  const [share, setShare] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [shareCust, setShareCust] = useState("none");

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    const [list, j, cs, sl] = await Promise.all([
      api.get("/completion-photos", { params }),
      api.get("/jobs"),
      api.get("/customers"),
      api.get("/slabs"),
    ]);
    setItems(list.data);
    setJobs(j.data);
    setCustomers(cs.data);
    setSlabs(sl.data);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  usePendingHdRefresh(
    hasPendingHd(items.map((i) => i.photo)),
    load,
  );

  const submit = async (e) => {
    e.preventDefault();
    if (files.length === 0) {
      toast.error("Add a photo first");
      return;
    }
    setBusy(true);
    try {
      for (const f of await shrinkAll(files)) {
        const fd = new FormData();
        fd.append("file", f);
        if (f.enhanced) fd.append("enhanced", "true");
        fd.append("job_id", form.job_id === "none" ? "" : form.job_id);
        fd.append("slab_color", form.slab_color);
        fd.append("description", form.description);
        fd.append("name", form.name);
        fd.append("category", form.category === "none" ? "" : form.category);
        fd.append("slab_id", form.slab_id === "none" ? "" : form.slab_id);
        await api.post("/completion-photos", fd);
      }
      toast.success(
        files.length === 1
          ? "Completion photo saved"
          : `${files.length} photos saved`,
      );
      setFiles([]);
      setShowForm(false);
      setForm({
        job_id: form.job_id,
        slab_color: "",
        slab_id: "none",
        description: "",
        name: "",
        category: form.category,
      });
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Upload failed",
      );
    }
    setBusy(false);
  };

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const shownPhotos = items.filter((i) => {
    const web =
      webFilter === "all"
        ? true
        : webFilter === "on"
          ? i.show_on_website
          : !i.show_on_website;
    const feat =
      featFilter === "all"
        ? true
        : featFilter === "on"
          ? i.featured
          : !i.featured;
    const cat = catFilter.length === 0 || catFilter.includes(i.category);
    const job = jobFilter.length === 0 || jobFilter.includes(i.job_id);
    return web && feat && cat && job;
  });

  const makeShare = async () => {
    try {
      const { data } = await api.post("/completion-photos/share", {
        photo_ids: picked,
        customer_id: shareCust === "none" ? null : shareCust,
      });
      const url = `${window.location.origin}${data.path}`;
      setShare({ token: data.token, url });
      toast.success("Share link ready");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not create link",
      );
    }
  };

  const emailShare = async () => {
    try {
      await api.post(`/completion-photos/share/${share.token}/email`);
      toast.success("Email sent to the customer");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Email failed",
      );
    }
  };

  const toggleWebsite = async (item) => {
    const show = !item.show_on_website;
    try {
      await api.patch(`/completion-photos/${item.id}/website`, { show });
      setItems((list) =>
        list.map((x) =>
          x.id === item.id ? { ...x, show_on_website: show } : x,
        ),
      );
      toast.success(
        show ? "Added to the website gallery" : "Taken off the website",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save that",
      );
    }
  };

  const bulkWebsite = async (show) => {
    try {
      await api.post("/completion-photos/website", {
        photo_ids: picked,
        show,
      });
      setItems((list) =>
        list.map((x) =>
          picked.includes(x.id) ? { ...x, show_on_website: show } : x,
        ),
      );
      toast.success(
        show
          ? `${picked.length} ${picked.length === 1 ? "photo" : "photos"} added to the website`
          : "Taken off the website",
      );
      setPicked([]);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not update the website gallery",
      );
    }
  };

  const toggleFeatured = async (item) => {
    const featured = !item.featured;
    try {
      await api.patch(`/completion-photos/${item.id}/featured`, { featured });
      setItems((list) =>
        list.map((x) => (x.id === item.id ? { ...x, featured } : x)),
      );
      toast.success(
        featured ? "Featured on the homepage" : "Removed from homepage feature",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save that",
      );
    }
  };

  const tagSlab = async (item, value) => {
    try {
      const { data } = await api.patch(`/completion-photos/${item.id}/slab`, {
        slab_id: value === "none" ? null : value,
      });
      setItems((list) =>
        list.map((x) =>
          x.id === item.id
            ? {
                ...x,
                slab_id: data.slab_id,
                product: data.product,
                material: data.material,
                slab_color: data.slab_color,
              }
            : x,
        ),
      );
      toast.success(value === "none" ? "Stone tag removed" : "Stone tagged");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not tag that",
      );
    }
  };

  const remove = async (item) => {
    await api.delete(`/completion-photos/${item.id}`);
    toast.success("Photo removed");
    load();
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Completion photos" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="completion-header-card"
        >
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Proof of finish
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
              Completion Photos
            </h1>
            <p
              className="mt-1 text-sm text-zinc-500"
              data-testid="completion-count"
            >
              {items.length} finished installs on file
            </p>
            <p className="mt-1 text-sm" data-testid="completion-live-count">
              <span className="text-emerald-400">
                {items.filter((i) => i.show_on_website).length} on the website
              </span>
              <a
                href="/gallery"
                target="_blank"
                rel="noreferrer"
                data-testid="completion-view-gallery"
                className="ml-3 text-xs uppercase tracking-widest text-indigo-300 hover:underline"
              >
                View the public gallery
              </a>
            </p>
          </div>

          <div
            className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4"
            data-testid="completion-actions"
          >
            <Button
              data-testid="open-completion-form"
              onClick={() => setShowForm(true)}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Camera size={15} weight="bold" className="mr-2" /> Add Photos
            </Button>
          </div>
        </div>

        {showForm && (
          <div
            className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 p-4 py-10"
            data-testid="completion-form-modal"
          >
            <form
              onSubmit={submit}
              data-testid="completion-form"
              className="grid w-full max-w-4xl gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5 lg:grid-cols-4"
            >
              <div className="flex items-start gap-3 lg:col-span-4">
                <h2 className="text-lg font-bold">Add completion photos</h2>
                <button
                  type="button"
                  data-testid="close-completion-form"
                  onClick={() => setShowForm(false)}
                  className="ml-auto text-zinc-500 hover:text-white"
                >
                  <X size={18} weight="bold" />
                </button>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  Job
                </Label>
                <Select
                  value={form.job_id}
                  onValueChange={(v) => setForm({ ...form, job_id: v })}
                >
                  <SelectTrigger
                    data-testid="completion-job-select"
                    className="h-12 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    <SelectItem value="none">No job</SelectItem>
                    {jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.code} — {j.project_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  Slab color
                </Label>
                <Input
                  data-testid="completion-color-input"
                  value={form.slab_color}
                  onChange={(e) =>
                    setForm({ ...form, slab_color: e.target.value })
                  }
                  placeholder="Calacatta Gold"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  Photo name
                </Label>
                <Input
                  data-testid="completion-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Elm St island"
                  className="h-12 border-zinc-800 bg-zinc-950"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  Category
                </Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger
                    data-testid="completion-category-select"
                    className="h-12 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue placeholder="Pick a category" />
                  </SelectTrigger>
                  <SelectContent className="border-zinc-800 bg-zinc-900">
                    <SelectItem value="none">No category</SelectItem>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  Stone used (inventory)
                </Label>
                <Select
                  value={form.slab_id}
                  onValueChange={(v) => setForm({ ...form, slab_id: v })}
                >
                  <SelectTrigger
                    data-testid="completion-slab-select"
                    className="h-12 border-zinc-800 bg-zinc-950"
                  >
                    <SelectValue placeholder="Tag the slab used" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 border-zinc-800 bg-zinc-900">
                    <SelectItem value="none">No stone tag</SelectItem>
                    {slabs.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.code} — {s.slab_color}
                        {s.material ? ` · ${s.material}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 lg:col-span-4">
                <Label className="text-xs uppercase tracking-widest text-zinc-500">
                  Description
                </Label>
                <Textarea
                  data-testid="completion-description-input"
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  rows={2}
                  placeholder="Kitchen island, mitred edge, undermount sink"
                  className="border-zinc-800 bg-zinc-950"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 lg:col-span-4">
                <Button
                  type="button"
                  data-testid="completion-add-photo"
                  variant="outline"
                  onClick={() => inputRef.current?.click()}
                  className="h-12 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
                >
                  <Camera size={16} weight="bold" className="mr-2" />
                  {files.length ? `Add More (${files.length})` : "Add Photos"}
                </Button>
                <input
                  ref={inputRef}
                  data-testid="completion-photo-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const picked = Array.from(e.target.files || []);
                    if (picked.length) setFiles((f) => [...f, ...picked]);
                    e.target.value = "";
                  }}
                />
                {files.length > 0 && (
                  <>
                    <div
                      className="flex flex-wrap gap-2"
                      data-testid="completion-previews"
                    >
                      {files.map((f, i) => (
                        <span key={`${f.name}-${i}`} className="relative">
                          <img
                            src={URL.createObjectURL(f)}
                            alt=""
                            data-testid={`completion-preview-${i}`}
                            className="h-16 w-24 rounded-md border border-zinc-800 object-cover"
                          />
                          <button
                            type="button"
                            data-testid={`completion-remove-preview-${i}`}
                            onClick={() =>
                              setFiles((list) => list.filter((_, x) => x !== i))
                            }
                            className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-zinc-800 text-zinc-300 hover:text-red-400"
                          >
                            <X size={11} weight="bold" />
                          </button>
                        </span>
                      ))}
                    </div>
                    {files.length === 1 && (
                      <Button
                        type="button"
                        data-testid="completion-edit-photo"
                        variant="outline"
                        onClick={() => setEditing(true)}
                        className="h-12 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
                      >
                        Crop / Remove BG
                      </Button>
                    )}
                  </>
                )}
                <Button
                  type="submit"
                  data-testid="save-completion-button"
                  disabled={busy}
                  className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                >
                  <CheckCircle size={16} weight="bold" className="mr-2" />
                  {busy
                    ? "Saving…"
                    : files.length > 1
                      ? `Save ${files.length} Photos`
                      : "Save Photo"}
                </Button>
                <Button
                  type="button"
                  data-testid="cancel-completion-form"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest hover:bg-zinc-800"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <FilterBar
          className="mt-8"
          testidPrefix="completion"
          search={{
            value: q,
            onChange: setQ,
            placeholder: "Search color, job, customer, description…",
            testid: "completion-search",
          }}
          sections={[
            {
              key: "website",
              label: "Website",
              chipPrefix: "Website: ",
              value: webFilter,
              allValue: "all",
              onChange: setWebFilter,
              testid: "completion-website-filter",
              options: [
                ["all", "All photos", "completion-website-all"],
                ["on", "On the website", "completion-website-on"],
                ["off", "Not published", "completion-website-off"],
              ],
            },
            {
              key: "featured",
              label: "Featured",
              chipPrefix: "Featured: ",
              value: featFilter,
              allValue: "all",
              onChange: setFeatFilter,
              testid: "completion-featured-filter",
              options: [
                ["all", "All photos", "completion-featured-all"],
                ["on", "Featured", "completion-featured-on"],
                ["off", "Not featured", "completion-featured-off"],
              ],
            },
            {
              key: "category",
              label: "Category",
              multi: true,
              values: catFilter,
              allValue: "all",
              onChange: setCatFilter,
              testid: "completion-category-filter",
              options: [
                ["all", "All categories", "completion-category-all"],
                ...CATEGORIES.map((c) => [
                  c,
                  c,
                  `completion-category-${c.toLowerCase()}`,
                ]),
              ],
            },
            {
              key: "job",
              label: "Job",
              chipPrefix: "Job: ",
              multi: true,
              values: jobFilter,
              allValue: "all",
              onChange: setJobFilter,
              testid: "completion-job-filter",
              options: [
                ["all", "All jobs", "completion-job-all"],
                ...jobs.map((j) => [
                  j.id,
                  `${j.code}${j.project_name ? ` — ${j.project_name}` : ""}`,
                  `completion-job-${j.code?.toLowerCase() || j.id}`,
                ]),
              ],
            },
          ]}
          right={
            <Button
              data-testid="completion-slideshow-button"
              disabled={items.length === 0}
              onClick={() => setSlide(0)}
              className="h-12 shrink-0 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Play size={15} weight="bold" className="mr-2" /> Slideshow
            </Button>
          }
        />

        {picked.length > 0 && (
          <div
            className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-yellow-500/40 bg-zinc-900 p-4"
            data-testid="share-bar"
          >
            <span className="text-xs uppercase tracking-widest text-zinc-400">
              {picked.length} selected
            </span>
            <Button
              data-testid="bulk-website-on"
              onClick={() => bulkWebsite(true)}
              className="h-11 bg-emerald-600 text-xs font-bold uppercase tracking-widest text-white hover:bg-emerald-500"
            >
              <Globe size={15} weight="bold" className="mr-2" /> Add To Website
            </Button>
            <Button
              data-testid="bulk-website-off"
              variant="outline"
              onClick={() => bulkWebsite(false)}
              className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
            >
              Remove From Website
            </Button>
            <Select value={shareCust} onValueChange={setShareCust}>
              <SelectTrigger
                data-testid="share-account-select"
                className="h-11 w-64 border-zinc-800 bg-zinc-950"
              >
                <SelectValue placeholder="Account" />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value="none">No account (link only)</SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              data-testid="create-share-button"
              onClick={makeShare}
              className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <ShareNetwork size={15} weight="bold" className="mr-2" /> Create
              Link
            </Button>
            {share && (
              <>
                <Button
                  data-testid="share-email-button"
                  variant="outline"
                  onClick={emailShare}
                  className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
                >
                  <EnvelopeSimple size={15} weight="bold" className="mr-2" />{" "}
                  Email
                </Button>
                <a
                  data-testid="share-text-button"
                  href={`sms:?&body=${encodeURIComponent(`Your finished project photos: ${share.url}`)}`}
                  className="inline-flex h-11 items-center gap-2 rounded-md border border-zinc-700 px-4 text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
                >
                  <ChatText size={15} weight="bold" /> Text
                </a>
                <Button
                  data-testid="share-copy-button"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard?.writeText(share.url);
                    toast.success("Link copied");
                  }}
                  className="h-11 border-zinc-700 bg-transparent text-xs font-semibold uppercase tracking-widest hover:bg-zinc-800"
                >
                  <Copy size={15} weight="bold" className="mr-2" /> Copy
                </Button>
                <span
                  className="font-mono-data w-full truncate text-xs text-zinc-500"
                  data-testid="share-url"
                >
                  {share.url}
                </span>
              </>
            )}
            <button
              data-testid="share-clear"
              onClick={() => {
                setPicked([]);
                setShare(null);
              }}
              className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white"
            >
              Clear
            </button>
          </div>
        )}

        {shownPhotos.length === 0 ? (
          <p className="mt-6 rounded-lg border border-dashed border-zinc-800 p-10 text-center text-sm text-zinc-500">
            {items.length === 0
              ? "No completion photos yet — snap one at the next install."
              : "No photos match that filter."}
          </p>
        ) : (
          <div
            className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="completion-grid"
          >
            {shownPhotos.map((i) => (
              <div
                key={i.id}
                data-testid={`completion-tile-${i.id}`}
                className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition-colors duration-150 hover:border-zinc-700"
              >
                <div className="relative">
                  <img
                    src={photoSrc(i.photo)}
                    alt={i.slab_color}
                    onClick={() => setViewer(i)}
                    className="h-48 w-full cursor-zoom-in object-cover"
                  />
                  {(i.photo?.enhanced || HD_STATUS_LABEL[i.photo?.enhance_status]) && (
                    <span
                      data-testid={`completion-hd-${i.id}`}
                      title={i.photo?.enhance_error || ""}
                      className={`pointer-events-none absolute right-2 top-2 rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                        i.photo?.enhance_status === "pending"
                          ? "animate-pulse bg-indigo-500 text-white"
                          : i.photo?.enhanced
                            ? "bg-emerald-500 text-black"
                            : "bg-zinc-800/90 text-zinc-300"
                      }`}
                    >
                      {i.photo?.enhanced && i.photo?.enhance_status !== "pending"
                        ? "HD"
                        : HD_STATUS_LABEL[i.photo?.enhance_status]}
                    </span>
                  )}
                  <button
                    data-testid={`completion-select-${i.id}`}
                    onClick={() => toggle(i.id)}
                    className={`absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-md border text-white transition-colors duration-150 ${
                      picked.includes(i.id)
                        ? "border-indigo-500 bg-indigo-500"
                        : "border-white/40 bg-black/50 hover:bg-black/70"
                    }`}
                  >
                    <CheckCircle size={16} weight="bold" />
                  </button>
                </div>
                <div className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-bold">
                        {i.name || i.slab_color || "Install"}
                      </h3>
                      {i.category && (
                        <span
                          data-testid={`completion-category-${i.id}`}
                          className="mt-1 inline-block rounded-full border border-indigo-400/40 bg-indigo-500/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-indigo-300"
                        >
                          {i.category}
                        </span>
                      )}
                      {i.name && i.slab_color && (
                        <p className="truncate text-xs text-zinc-500">
                          {i.slab_color}
                        </p>
                      )}
                      {i.job_code && (
                        <Link
                          to={`/projects/${i.job_id}?tab=field`}
                          data-testid={`completion-job-link-${i.id}`}
                          className="font-mono-data text-xs text-yellow-400 hover:underline"
                        >
                          {i.job_code} {i.project_name}
                        </Link>
                      )}
                    </div>
                    <button
                      data-testid={`completion-delete-${i.id}`}
                      onClick={() => remove(i)}
                      className="shrink-0 text-zinc-600 hover:text-red-400"
                    >
                      <Trash size={15} weight="bold" />
                    </button>
                  </div>
                  {i.description && (
                    <p className="mt-2 line-clamp-2 text-xs text-zinc-400">
                      {i.description}
                    </p>
                  )}
                  <p className="mt-2 text-[11px] uppercase tracking-widest text-zinc-600">
                    {i.created_at?.slice(0, 10)}
                  </p>
                  <div
                    className="mt-3"
                    data-testid={`completion-stone-${i.id}`}
                  >
                    <Select
                      value={i.slab_id || "none"}
                      onValueChange={(v) => tagSlab(i, v)}
                    >
                      <SelectTrigger
                        data-testid={`completion-stone-select-${i.id}`}
                        className="h-9 border-zinc-800 bg-zinc-950 text-xs"
                      >
                        <SelectValue placeholder="Tag stone used" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72 border-zinc-800 bg-zinc-900">
                        <SelectItem value="none">No stone tag</SelectItem>
                        {slabs.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.code} — {s.slab_color}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {i.product && (
                      <p
                        className="mt-1 text-[11px] text-zinc-500"
                        data-testid={`completion-product-${i.id}`}
                      >
                        Made with {i.product}
                      </p>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      data-testid={`completion-story-${i.id}`}
                      onClick={() => setStory(i)}
                      title="Write the case study: story, edge profile, sink"
                      className={`flex h-9 flex-1 items-center justify-center gap-2 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                        i.story || i.edge_profile || i.sink
                          ? "border-sky-500/50 bg-sky-500/15 text-sky-300"
                          : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
                      }`}
                    >
                      <NotePencil size={13} weight="bold" />
                      {i.story ? "Case study" : "Add story"}
                    </button>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      data-testid={`completion-website-${i.id}`}
                      onClick={() => toggleWebsite(i)}
                      className={`flex h-9 flex-1 items-center justify-center gap-2 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                        i.show_on_website
                          ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                          : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
                      }`}
                    >
                      <Globe size={13} weight="bold" />
                      {i.show_on_website ? "On website" : "Show on site"}
                    </button>
                    <button
                      type="button"
                      data-testid={`completion-feature-${i.id}`}
                      onClick={() => toggleFeatured(i)}
                      title={
                        i.featured
                          ? "Featured on homepage"
                          : "Feature on homepage"
                      }
                      className={`flex h-9 w-11 shrink-0 items-center justify-center rounded-md border transition-colors duration-150 ${
                        i.featured
                          ? "border-yellow-500/50 bg-yellow-500/15 text-yellow-300"
                          : "border-zinc-800 text-zinc-500 hover:text-zinc-200"
                      }`}
                    >
                      <Star size={14} weight={i.featured ? "fill" : "bold"} />
                    </button>
                  </div>
                  <BeforePhotoControl
                    item={i}
                    onChange={(doc) =>
                      setItems((list) => list.map((x) => (x.id === doc.id ? doc : x)))
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {editing && files.length === 1 && (
        <PhotoEditor
          file={files[0]}
          onDone={(f) => {
            setFiles([f]);
            setEditing(false);
          }}
          onClose={() => setEditing(false)}
        />
      )}

      {story && (
        <ProjectStoryDialog
          item={story}
          onClose={() => setStory(null)}
          onSaved={(doc) => {
            setItems((list) => list.map((x) => (x.id === doc.id ? doc : x)));
            setStory(null);
          }}
        />
      )}

      {slide !== null && items.length > 0 && (
        <Slideshow items={items} start={slide} onClose={() => setSlide(null)} />
      )}

      {viewer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
          data-testid="completion-viewer"
          onClick={() => setViewer(null)}
        >
          <img
            src={photoSrc(viewer.photo)}
            alt=""
            className="max-h-[85vh] max-w-full object-contain"
          />
          <button
            data-testid="completion-viewer-close"
            onClick={() => setViewer(null)}
            className="absolute right-6 top-6 text-white"
          >
            <X size={22} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}
